// ─── MessageCenterPage ───────────────────────────────────────────────────────
// Project-scoped chat-style feed, mirroring the XD reference exactly:
// page header (Message Center + bell + search), then a vertical card list
// of messages (newest first), then a compose box pinned to the bottom
// with a small formatting toolbar (B/I/list/numbered/U/link) and Send.
//
// Project resolution: takes the last-viewed projectId from localStorage
// (set by DashboardPage when the user picks a project). Without one we
// show an "open a project first" empty state.
//
// Header chrome matches the dashboard's other pages — Navigation (blue
// top bar always; marketing nav when logged out) plus AppHeader (the
// FONDRIEST logo + Dashboard/Data Analytics/Report/Gallery/Settings/Live
// tabs) when authenticated.

import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
    Bell, BellOff, Search, MoreVertical, Bold, Italic, Underline, List, ListOrdered,
    Link as LinkIcon, Send, CalendarClock, Loader, Trash2, Check, ChevronUp,
    ChevronDown, AlertCircle,
} from 'lucide-react'
import { Navigation } from '../components/Navigation'
import { AppHeader } from '../components/AppHeader'
import useAuthStore from '../store/auth-store'
import {
    listMessages, createMessage, deleteMessage,
    markMessageRead, markAllRead,
    getNotificationPrefs, updateNotificationPrefs,
} from '../lib/messageApi'

const fmtTs = (iso) => {
    if (!iso) return ''
    const d = new Date(iso)
    if (isNaN(d.getTime())) return String(iso)
    const pad = (n) => String(n).padStart(2, '0')
    const date = `${pad(d.getMonth()+1)}/${pad(d.getDate())}/${d.getFullYear()}`
    let hours = d.getHours()
    const m12 = hours >= 12 ? 'pm' : 'am'
    hours = hours % 12; if (hours === 0) hours = 12
    const time = `${hours}:${pad(d.getMinutes())}${m12}`
    // Local-tz suffix — best-effort. Browsers can return "EST"/"EDT" via
    // Intl.DateTimeFormat with timeZoneName: 'short'.
    let tz = ''
    try {
        const parts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(d)
        tz = parts.find((p) => p.type === 'timeZoneName')?.value || ''
    } catch {}
    return `${date} ${time}${tz ? ` ${tz}` : ''}`
}

// Project id resolved from either the URL (when this page is mounted under
// /messages/:projectId) or the per-user "last viewed" id we already persist.
const lastProjectId = () => {
    try {
        const v = localStorage.getItem('wq_last_project_id')
        return v ? Number(v) : null
    } catch { return null }
}

// Tiny formatting helpers for the compose toolbar. document.execCommand is
// deprecated but still the simplest way to apply inline formatting inside
// a contenteditable without pulling in a 50kB rich-text library.
const applyFormat = (cmd, arg = null) => {
    try { document.execCommand(cmd, false, arg) } catch {}
}

export default function MessageCenterPage() {
    const navigate = useNavigate()
    const params   = useParams()
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
    const currentUserId   = useAuthStore((s) => s.user?.id)
    const isSuperUser     = useAuthStore((s) => !!(s.user?.is_superuser || s.user?.is_staff))
    const currentUserName = useAuthStore((s) => s.user?.first_name || s.user?.username || '')
    // Everything the mentions filter treats as "you" — first name, last
    // name, full name, username, and email local-part. Matched
    // case-insensitively against `@…` tokens in each message body so
    // "@Ajit", "@ajit_jain", or "@AjitJain" all count.
    const mentionAliases  = useAuthStore((s) => {
        const u = s.user || {}
        const localPart = String(u.email || '').split('@')[0]
        const full      = [u.first_name, u.last_name].filter(Boolean).join(' ')
        const fullCamel = [u.first_name, u.last_name].filter(Boolean).join('')
        return [u.first_name, u.last_name, full, fullCamel, u.username, u.name, localPart]
            .filter(Boolean).map((s) => String(s).trim()).filter(Boolean)
    })

    const urlProjectId = params.projectId ? Number(params.projectId) : null
    const projectId = urlProjectId || lastProjectId()

    const [messages, setMessages] = useState([])
    const [loading,  setLoading]  = useState(false)
    const [error,    setError]    = useState(null)
    const [query,    setQuery]    = useState('')
    const [openMenuId, setOpenMenuId] = useState(null)
    // Bell dropdown: open/closed + the user's stored preferences. `prefs`
    // is loaded async from /messages/preferences/ on first bell click.
    const [bellOpen,   setBellOpen]   = useState(false)
    const [muteSubOpen, setMuteSubOpen] = useState(false)
    const [prefs,      setPrefs]      = useState(null) // { scope, mute_until, is_muted } | null
    const composeRef = useRef(null)

    // Client-side mentions filter — driven by the bell dropdown's
    // "Mentions Only" preference (prefs.scope === 'mentions'). Matches
    // `@<Alias>` anywhere in the rendered body, case-insensitively,
    // boundary-aware so "@ajit" doesn't match "@ajittest". Strips HTML
    // first so tags inside the body can't create false positives.
    const mentionsOnly = prefs?.scope === 'mentions'
    const visibleMessages = useMemo(() => {
        if (!mentionsOnly || !mentionAliases.length) return messages
        const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const pattern = new RegExp(
            `@(?:${mentionAliases.map(escape).join('|')})\\b`,
            'i',
        )
        return messages.filter((m) => {
            const plain = String(m.body || '').replace(/<[^>]+>/g, ' ')
            return pattern.test(plain)
        })
    }, [messages, mentionsOnly, mentionAliases])

    // Initial load + reload when query changes (debounced).
    useEffect(() => {
        if (!projectId) return
        const t = setTimeout(async () => {
            setLoading(true); setError(null)
            try {
                const rows = await listMessages(projectId, query.trim())
                setMessages(rows)
            } catch (e) {
                setError(e?.response?.data?.message || e?.message || 'Failed to load messages')
            } finally {
                setLoading(false)
            }
        }, query ? 250 : 0)
        return () => clearTimeout(t)
    }, [projectId, query])

    // Close any open per-message kebab menu / bell dropdown on outside click.
    useEffect(() => {
        if (openMenuId == null && !bellOpen) return
        const onDoc = () => { setOpenMenuId(null); setBellOpen(false); setMuteSubOpen(false) }
        document.addEventListener('mousedown', onDoc)
        return () => document.removeEventListener('mousedown', onDoc)
    }, [openMenuId, bellOpen])

    // Load notification preferences the first time the bell is opened.
    // Skipped when prefs already cached — they don't change often enough
    // to justify polling.
    useEffect(() => {
        if (!bellOpen || prefs != null) return
        getNotificationPrefs()
            .then(setPrefs)
            .catch(() => setPrefs({ scope: 'all', mute_until: null, is_muted: false }))
    }, [bellOpen, prefs])

    const onPickScope = async (scope) => {
        // Optimistic update — flip the radio dot immediately, roll back
        // if the PATCH 4xx's. Same pattern is used across the app.
        const prev = prefs
        setPrefs((p) => ({ ...(p || {}), scope }))
        try {
            const fresh = await updateNotificationPrefs({ scope })
            setPrefs(fresh)
        } catch {
            setPrefs(prev)
        }
    }
    const onMute = async (mute, customIso = null) => {
        setMuteSubOpen(false); setBellOpen(false)
        try {
            const fresh = await updateNotificationPrefs(
                mute === 'custom' && customIso
                    ? { mute: 'custom', mute_until_iso: customIso }
                    : { mute }
            )
            setPrefs(fresh)
        } catch (e) {
            alert(e?.response?.data?.message || e?.message || 'Failed to update mute setting')
        }
    }
    const onMuteCustom = () => {
        // Cheap prompt for "Custom" — full date+time picker UI is overkill
        // for the bell dropdown. ISO datetime-local string converted to
        // UTC ISO for the server.
        const raw = window.prompt('Mute until (YYYY-MM-DD HH:MM, your local time):', '')
        if (!raw) return
        const local = new Date(raw.replace(' ', 'T'))
        if (isNaN(local.getTime())) { alert('Invalid date/time'); return }
        onMute('custom', local.toISOString())
    }
    const onMarkRead = async (m) => {
        setOpenMenuId(null)
        // Optimistic — flip the dot, then call the API. Idempotent on the
        // server, so a second call is harmless.
        setMessages((rows) => rows.map((r) => r.id === m.id ? { ...r, is_read: true } : r))
        try {
            await markMessageRead(m.id)
            // Nudge the Navigation badge to refresh now instead of waiting
            // for the next poll tick.
            window.dispatchEvent(new Event('wq_messages_changed'))
        } catch {
            // Roll back on error.
            setMessages((rows) => rows.map((r) => r.id === m.id ? { ...r, is_read: false } : r))
        }
    }

    const onSend = async () => {
        const el = composeRef.current
        if (!el) return
        const body = el.innerHTML.trim()
        if (!body || !el.textContent.trim()) return
        if (!projectId) { setError('Open a project first.'); return }
        try {
            const fresh = await createMessage({ projectId, body })
            setMessages((m) => [fresh, ...m])
            el.innerHTML = ''
            window.dispatchEvent(new Event('wq_messages_changed'))
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || 'Failed to send message')
        }
    }
    const onSendKey = (e) => {
        // Cmd/Ctrl+Enter sends, Enter alone inserts a newline (matches the XD
        // behaviour — toolbar implies rich text, not single-line submit).
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault(); onSend()
        }
    }
    const onSchedule = () => {
        // The second blue button in the XD shows a calendar icon — placeholder
        // for "schedule send". Hook up to whatever scheduler we add later.
        alert('Schedule send is coming soon.')
    }

    const onAddLink = () => {
        const url = window.prompt('Link URL', 'https://')
        if (!url) return
        applyFormat('createLink', url)
    }

    const onDelete = async (m) => {
        setOpenMenuId(null)
        if (!window.confirm('Delete this message? This cannot be undone.')) return
        try {
            await deleteMessage(m.id)
            setMessages((rows) => rows.filter((r) => r.id !== m.id))
        } catch (e) {
            alert(e?.response?.data?.message || e?.message || 'Failed to delete message')
        }
    }

    // The blue-tinted background highlights UNREAD messages (per requesting
    // user). Once "Mark As Read" is clicked the highlight drops, mirroring
    // the XD interaction model.
    // eslint-disable-next-line no-unused-vars
    const _newestId = messages[0]?.id ?? null

    return (
        <div className="w-full flex flex-col min-h-screen bg-[#F1F2F4]">
            <Navigation />
            {isAuthenticated && <AppHeader />}

            <main className="flex-1 px-4 md:px-8 py-6">
                <div className="max-w-10xl mx-auto bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col" style={{ minHeight: 'calc(100vh - 200px)' }}>
                    {/* Header: title + bell + search */}
                    <header className="break-sec-m px-4 py-3 md:px-6 md:py-4 border-b border-gray-200 flex items-center justify-between gap-4">
                        <h1 className="text-xl font-bold text-gray-800">Message Center</h1>
                        <div className="flex items-center gap-3 flex-1 max-w-md">
                            {/* Notifications bell — toggles the prefs dropdown.
                                Shows BellOff icon when notifications are
                                currently muted (mute_until in the future). */}
                            <div className="relative" onMouseDown={(e) => e.stopPropagation()}>
                                <button
                                    type="button"
                                    title={prefs?.is_muted ? 'Notifications muted' : 'Notifications'}
                                    onClick={() => setBellOpen((o) => !o)}
                                    className="text-gray-600 hover:text-[#358ECD] p-1.5 rounded"
                                >
                                    {prefs?.is_muted
                                        ? <BellOff className="w-5 h-5" />
                                        : <Bell className="w-5 h-5" />}
                                </button>
                                {bellOpen && (
                                    <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded shadow-xl z-30 py-2 text-sm">
                                        <p className="px-4 py-1 text-gray-500 text-xs">Notify you about…</p>
                                        {/* Default to "All" while prefs are still loading
                                            (or never set) so the dropdown never renders
                                            with both radios blank. */}
                                        <BellOption
                                            checked={(prefs?.scope || 'all') === 'all'}
                                            onClick={() => onPickScope('all')}
                                        >
                                            All New Messages
                                        </BellOption>
                                        <BellOption
                                            checked={prefs?.scope === 'mentions'}
                                            onClick={() => onPickScope('mentions')}
                                        >
                                            Mentions Only
                                        </BellOption>
                                        <div className="relative">
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); setMuteSubOpen((o) => !o) }}
                                                className="w-full flex items-center justify-between px-4 py-2 hover:bg-gray-50 text-gray-700"
                                            >
                                                <span>More Options</span>
                                                {muteSubOpen
                                                    ? <ChevronUp className="w-4 h-4 text-gray-500" />
                                                    : <ChevronDown className="w-4 h-4 text-gray-500" />}
                                            </button>
                                            {muteSubOpen && (
                                                <div className="absolute left-full top-0 ml-1 w-48 bg-white border border-gray-200 rounded shadow-xl z-40 py-2 text-sm">
                                                    <p className="px-4 py-1 text-gray-500 text-xs">Temporarily mute…</p>
                                                    <MuteRow onClick={() => onMute('until_tomorrow')}>Until Tomorrow</MuteRow>
                                                    <MuteRow onClick={() => onMute('1_hour')}>1 hour</MuteRow>
                                                    <MuteRow onClick={onMuteCustom}>Custom</MuteRow>
                                                    {prefs?.is_muted && (
                                                        <>
                                                            <div className="border-t border-gray-100 my-1" />
                                                            <MuteRow onClick={() => onMute('off')}>Unmute</MuteRow>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="relative flex-1">
                                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search"
                                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:border-[#358ECD]"
                                />
                            </div>
                        </div>
                    </header>

                    {/* Body — message feed */}
                    <div className="flex-1 overflow-y-auto px-4 py-3 md:px-6 md:py-4 space-y-3">
                        {!projectId && (
                            <div className="py-12 text-center text-sm text-gray-500">
                                Open a project first to see its messages.
                                <button onClick={() => navigate('/dashboard')}
                                        className="ml-2 text-[#358ECD] hover:underline">
                                    Go to dashboard
                                </button>
                            </div>
                        )}
                        {error && (
                            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
                                <AlertCircle className="w-4 h-4" /> {error}
                            </div>
                        )}
                        {projectId && loading && messages.length === 0 && (
                            <div className="py-12 text-center text-sm text-gray-500 inline-flex items-center justify-center gap-2 w-full">
                                <Loader className="w-4 h-4 animate-spin" /> Loading messages…
                            </div>
                        )}
                        {projectId && !loading && messages.length === 0 && (
                            <div className="py-12 text-center text-sm text-gray-400 italic">
                                No messages yet — be the first to post below.
                            </div>
                        )}
                        {projectId && !loading && messages.length > 0 && visibleMessages.length === 0 && mentionsOnly && (
                            <div className="py-12 text-center text-sm text-gray-500">
                                No messages mention you yet.
                                <button onClick={() => onPickScope('all')}
                                        className="ml-2 text-[#358ECD] hover:underline">
                                    Show all messages
                                </button>
                            </div>
                        )}

                        {visibleMessages.map((m) => {
                            const isUnread = !m.is_read
                            return (
                                <article
                                    key={m.id}
                                    className={`relative rounded-md px-4 py-3 border ${
                                        isUnread
                                            ? 'bg-[#EEF3F6] border-[#E2E8EC]'
                                            : 'bg-white border-gray-100'
                                    }`}
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="min-w-0">
                                            <p className="text-sm">
                                                <span className="font-semibold text-gray-800">{m.author_name}</span>
                                                <span className="text-gray-500 ml-3 text-xs">{fmtTs(m.created_at)}</span>
                                            </p>
                                            <div
                                                className="prose prose-sm max-w-none text-gray-800 mt-1 break-words [&_a]:text-[#358ECD] [&_a]:underline"
                                                /* Backend already sanitises the body to an allow-list. */
                                                dangerouslySetInnerHTML={{ __html: m.body }}
                                            />
                                        </div>
                                        {/* Per-message kebab menu. Hide entirely when the requesting
                                            user can't perform any action on this message (already-read
                                            + not the author + not a superuser). */}
                                        <div className="relative" onMouseDown={(e) => e.stopPropagation()}>
                                            {(() => {
                                                // Strict self-only rule: Delete is offered ONLY when
                                                // the requesting user authored the message. The backend
                                                // additionally allows superusers, but the UI doesn't
                                                // surface that exception — matches the user-facing rule
                                                // "delete only your own messages".
                                                const canDelete = currentUserId != null && Number(m.author_id) === Number(currentUserId)
                                                const canMarkRead = !m.is_read
                                                if (!canDelete && !canMarkRead) return null
                                                return (
                                                    <button
                                                        type="button"
                                                        onClick={() => setOpenMenuId(openMenuId === m.id ? null : m.id)}
                                                        className="p-1 text-gray-400 hover:text-gray-700 rounded"
                                                    >
                                                        <MoreVertical className="w-4 h-4" />
                                                    </button>
                                                )
                                            })()}
                                            {openMenuId === m.id && (() => {
                                                // Delete is only offered when the requesting user authored
                                                // the message (or is a staff/superuser). Backend enforces
                                                // the same rule, so this is purely UX. Both Mark As Read
                                                // and Delete are conditional — when neither applies we
                                                // still render the kebab but the dropdown stays empty,
                                                // which we collapse here so nothing pops open.
                                                // Strict self-only rule: Delete is offered ONLY when
                                                // the requesting user authored the message. The backend
                                                // additionally allows superusers, but the UI doesn't
                                                // surface that exception — matches the user-facing rule
                                                // "delete only your own messages".
                                                const canDelete = currentUserId != null && Number(m.author_id) === Number(currentUserId)
                                                const canMarkRead = !m.is_read
                                                if (!canDelete && !canMarkRead) return null
                                                return (
                                                    <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded shadow-lg z-10 text-xs">
                                                        {canMarkRead && (
                                                            <button
                                                                onClick={() => onMarkRead(m)}
                                                                className="w-full text-left px-3 py-2 hover:bg-gray-50 inline-flex items-center gap-1.5 text-gray-700"
                                                            >
                                                                <Check className="w-3.5 h-3.5" /> Mark As Read
                                                            </button>
                                                        )}
                                                        {canDelete && (
                                                            <button
                                                                onClick={() => onDelete(m)}
                                                                className={`w-full text-left px-3 py-2 hover:bg-gray-50 inline-flex items-center gap-1.5 text-red-600 ${canMarkRead ? 'border-t border-gray-100' : ''}`}
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" /> Delete
                                                            </button>
                                                        )}
                                                    </div>
                                                )
                                            })()}
                                        </div>
                                    </div>
                                </article>
                            )
                        })}
                    </div>

                    {/* Compose box */}
                    <div className="border-t border-gray-200 bg-[#F8F9FA]">
                        <div className="px-3 py-2 border-b border-gray-200 flex items-center gap-1">
                            <ToolbarBtn title="Bold (Cmd/Ctrl+B)"        onClick={() => applyFormat('bold')}>     <Bold className="w-4 h-4" />     </ToolbarBtn>
                            <ToolbarBtn title="Italic (Cmd/Ctrl+I)"      onClick={() => applyFormat('italic')}>   <Italic className="w-4 h-4" />   </ToolbarBtn>
                            <ToolbarBtn title="Bullet list" onClick={() => { composeRef.current?.focus(); applyFormat('insertUnorderedList') }}>
                                <List className="w-4 h-4" />
                            </ToolbarBtn>
                            <ToolbarBtn title="Numbered list" onClick={() => { composeRef.current?.focus(); applyFormat('insertOrderedList') }}>
                                <ListOrdered className="w-4 h-4" />
                            </ToolbarBtn>
                            <ToolbarBtn title="Underline (Cmd/Ctrl+U)"   onClick={() => applyFormat('underline')}>
                                <Underline className="w-4 h-4" />
                            </ToolbarBtn>
                            <ToolbarBtn title="Insert link"              onClick={onAddLink}>
                                <LinkIcon className="w-4 h-4" />
                            </ToolbarBtn>
                        </div>
                        <div className="px-3 py-3 flex items-end gap-3">
                            <div
                                ref={composeRef}
                                contentEditable
                                onKeyDown={onSendKey}
                                role="textbox"
                                aria-label="Compose a message"
                                data-placeholder="Enter message by typing..."
                                className="message-compose flex-1 min-h-[44px] max-h-[160px] overflow-y-auto px-3 py-2 text-sm bg-white border border-gray-200 rounded focus:outline-none focus:border-[#358ECD]"
                                suppressContentEditableWarning
                            />
                            <button
                                type="button"
                                onClick={onSend}
                                title="Send (Cmd/Ctrl+Enter)"
                                className="px-3 py-2 bg-[#358ECD] text-white rounded-l hover:bg-[#2A78B0]"
                            >
                                <Send className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                onClick={onSchedule}
                                title="Schedule send"
                                className="px-3 py-2 bg-[#358ECD] text-white rounded-r border-l border-white/30 hover:bg-[#2A78B0] -ml-3"
                            >
                                <CalendarClock className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
                <style>{`
                    .message-compose[contenteditable=""][data-placeholder]:empty::before,
                    .message-compose[contenteditable="true"][data-placeholder]:empty::before {
                        content: attr(data-placeholder);
                        color: #9ca3af;
                    }
                `}</style>
            </main>
        </div>
    )
}

function BellOption({ checked, onClick, children }) {
    // Behaves like a single-select group (matches the XD design — picking
    // one option flips the other off). Server enforces the two-value
    // allow-list so we don't need a radio-group wrapper.
    return (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClick?.() }}
            className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-50 text-gray-700 text-left"
        >
            <span className={`flex items-center justify-center w-4 h-4 rounded-sm border ${
                checked ? 'bg-[#358ECD] border-[#358ECD] text-white' : 'bg-white border-gray-400'
            }`}>
                {checked && <Check className="w-3 h-3" />}
            </span>
            <span>{children}</span>
        </button>
    )
}
function MuteRow({ onClick, children }) {
    return (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClick?.() }}
            className="w-full text-left px-4 py-2 hover:bg-gray-50 text-gray-700"
        >
            {children}
        </button>
    )
}

function ToolbarBtn({ title, onClick, children }) {
    return (
        <button
            type="button"
            onMouseDown={(e) => e.preventDefault() /* keep contenteditable focus */}
            onClick={onClick}
            title={title}
            className="p-1.5 text-gray-600 hover:text-[#358ECD] hover:bg-white rounded"
        >
            {children}
        </button>
    )
}
